import { useCallback, useEffect, useState } from "react";

const buildStorageKey = (clienteId) => `funil-acompanhamento-grupos:${clienteId}`;

export const readFunnelGroups = (clienteId) => {
  if (clienteId == null) return null;
  try {
    const raw = localStorage.getItem(buildStorageKey(clienteId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeFunnelGroups = (clienteId, groups) => {
  if (clienteId == null) return;
  try {
    localStorage.setItem(buildStorageKey(clienteId), JSON.stringify(groups));
  } catch {
    /* noop */
  }
};

export const updateFunnelGroup = (clienteId, groupId, partial) => {
  const stored = readFunnelGroups(clienteId);
  if (!Array.isArray(stored)) return null;
  const next = stored.map((group) =>
    group.id === groupId ? { ...group, ...partial } : group
  );
  writeFunnelGroups(clienteId, next);
  return next;
};

export const removeFunnelGroup = (clienteId, groupId) => {
  const stored = readFunnelGroups(clienteId);
  if (!Array.isArray(stored)) return null;
  const next = stored.filter((group) => group.id !== groupId);
  writeFunnelGroups(clienteId, next);
  return next;
};

const useFunnelMappingGroups = (clienteId, defaultGroups) => {
  const [groups, setGroups] = useState(() => {
    const stored = readFunnelGroups(clienteId);
    return Array.isArray(stored) ? stored : defaultGroups;
  });

  useEffect(() => {
    const stored = readFunnelGroups(clienteId);
    if (Array.isArray(stored)) {
      setGroups(stored);
    } else {
      setGroups(defaultGroups);
      writeFunnelGroups(clienteId, defaultGroups);
    }
  }, [clienteId, defaultGroups]);

  const updateGroup = useCallback(
    (groupId, partial) => {
      setGroups((prev) => {
        const next = prev.map((group) =>
          group.id === groupId ? { ...group, ...partial } : group
        );
        writeFunnelGroups(clienteId, next);
        return next;
      });
    },
    [clienteId]
  );

  const removeGroup = useCallback(
    (groupId) => {
      setGroups((prev) => {
        const next = prev.filter((group) => group.id !== groupId);
        writeFunnelGroups(clienteId, next);
        return next;
      });
    },
    [clienteId]
  );

  const applyItemEdits = useCallback(
    (edits) => {
      if (!edits || Object.keys(edits).length === 0) return;
      setGroups((prev) => {
        const next = prev.map((group) => {
          const updatedItems = group.items.map((item) => {
            const edit = edits[item.id];
            if (!edit) return item;
            return { ...item, ...edit };
          });

          const datesFromItems = updatedItems
            .map((item) => item.proximaNegociacao)
            .filter(Boolean);

          let groupProximaNegociacao = group.proximaNegociacao;
          if (datesFromItems.length > 0) {
            const parseDate = (str) => {
              const [d, m] = (str || "").split("/").map(Number);
              return m * 100 + d;
            };
            groupProximaNegociacao = datesFromItems.reduce((min, cur) =>
              parseDate(cur) < parseDate(min) ? cur : min
            );
          }

          return { ...group, items: updatedItems, proximaNegociacao: groupProximaNegociacao };
        });
        writeFunnelGroups(clienteId, next);
        return next;
      });
    },
    [clienteId]
  );

  return { groups, updateGroup, removeGroup, applyItemEdits };
};

export default useFunnelMappingGroups;
